import { ProductGroup } from '../types/ProductGroup';
import { GetProductGroupListingSchema } from '../schemas/ProductGroupZodSchema';
import { ProductGroupHubspotMappingForYageoContactUs } from '../types/productGroupHubspotMapping';
import { ProductGroupSchema, ProductSubroupSchema } from '../schemas/ProductGroupZodSchema';
import { z } from 'zod';

function makeYageoContactUsProductGroupMapping(
    productGroup: z.infer<typeof ProductGroupSchema> | z.infer<typeof ProductSubroupSchema>
): ProductGroupHubspotMappingForYageoContactUs {
    return productGroup.hubspotYageoContactUs_enabled
        ? {
              enabled: true as const,
              fieldValue: productGroup.hubspotYageoContactUs_fieldValue!,
              subgroupFieldName: productGroup.hubspotYageoContactUs_subgroupFieldName ?? null,
          }
        : {
              enabled: false as const,
              fieldValue: productGroup.hubspotYageoContactUs_fieldValue ?? null,
              subgroupFieldName: productGroup.hubspotYageoContactUs_subgroupFieldName ?? null,
          };
}

/**
 * Transforms and validates the response from the getProductGroupListing GraphQL query
 * to a simplified ProductGroup type.
 *
 * @param data The response data from the getProductGroupListing query
 * @returns An array of valid ProductGroup objects
 */
export function transformProductGroupResponse(
    data?: unknown // GetProductGroupListingQuery - use unknown for jest tests be
    // able to pass invalid data
): ProductGroup[] {
    // Parse and validate the data using the Zod schema
    const parsedResult = GetProductGroupListingSchema.safeParse(data);

    if (!parsedResult.success) {
        console.error('Validation failed for getProductGroupListing:', parsedResult.error.errors);
        return [];
    }

    const parsedGroups = parsedResult.data.getObjectFolder.children;

    // Transform segments to simplified ProductGroup type
    const groups: ProductGroup[] = [];
    parsedGroups.forEach((parsedGroup) => {
        const group: ProductGroup = {
            id: parsedGroup.id,
            key: parsedGroup.key,
            name: parsedGroup.name,
            shortBlurb: parsedGroup.shortBlurb,
            longBlurb: parsedGroup.longBlurb,
            dmDefinitionId: parsedGroup.dmDefinitionId,
            imageUrl: parsedGroup.image?.image.fullpath ?? null,
            backgroundImageUrl: parsedGroup.backgroundImage?.image.fullpath ?? null,
            children: [],
            hubspot: {
                yageoContactUs: makeYageoContactUsProductGroupMapping(parsedGroup),
            },
        };
        group.children = parsedGroup.productSubgroups.map((subgroup) => ({
            id: subgroup.id,
            key: subgroup.key,
            name: subgroup.name,
            shortBlurb: subgroup.shortBlurb,
            longBlurb: subgroup.longBlurb,
            dmDefinitionId: subgroup.dmDefinitionId,
            imageUrl: subgroup.image?.image.fullpath ?? null,
            backgroundImageUrl: subgroup.backgroundImage?.image.fullpath ?? null,
            parent: group,
            hubspot: {
                yageoContactUs: makeYageoContactUsProductGroupMapping(subgroup),
            },
        }));
        groups.push(group);
    });

    return groups;
}
